import {
  getFilterPriceBounds,
  getFilterPriceRawValueFromState,
  isFilterPriceRangeReady,
  toFilterPriceState,
  type TFilterPriceState,
  type TRawPrice,
} from '@/features/filter/price/lib/filterPriceRange';
import { tripsterStandard } from '@experience/money-utils';
import { type MaybeRefOrGetter, computed, ref, toValue, watch } from 'vue';

interface UseFilterPriceContentOptions {
  priceState: { price: TFilterPriceState };
  minPrice: MaybeRefOrGetter<number | undefined>;
  maxPrice: MaybeRefOrGetter<number | undefined>;
  histogram: MaybeRefOrGetter<Record<string, number> | undefined>;
  t: (key: string, params?: { price?: unknown }) => string;
}

export function useFilterPriceContent(options: UseFilterPriceContentOptions) {
  const isPriceRangeReady = computed(() =>
    isFilterPriceRangeReady(toValue(options.minPrice), toValue(options.maxPrice), toValue(options.histogram)),
  );

  const priceBounds = computed(() => getFilterPriceBounds(toValue(options.minPrice), toValue(options.maxPrice)));

  const getRawValueFromState = (): TRawPrice =>
    getFilterPriceRawValueFromState(options.priceState.price, priceBounds.value);

  const rawValue = ref<TRawPrice>(getRawValueFromState());

  const fromPricePlaceholder = computed(() => {
    const minPrice = toValue(options.minPrice);
    const priceFmt = minPrice ? tripsterStandard(minPrice) : minPrice;

    return options.t('price.fromPriceTitle', { price: priceFmt });
  });

  const toPricePlaceholder = computed(() => {
    const maxPrice = toValue(options.maxPrice);
    const priceFmt = maxPrice ? tripsterStandard(maxPrice) : maxPrice;

    return options.t('price.toPriceTitle', { price: priceFmt });
  });

  const formattedPrices = computed(() =>
    rawValue.value.map((price) => {
      if (typeof price === 'number' && Number.isFinite(price)) {
        return tripsterStandard(price);
      }

      return '';
    }),
  );

  const updateStore = () => {
    const bounds = priceBounds.value;

    if (!bounds) {
      return;
    }

    options.priceState.price = toFilterPriceState(rawValue.value, bounds);
  };

  const updateRawValue = (value: string, index: number) => {
    const newValueForRaw = [...rawValue.value] as TRawPrice;
    const parseValue = value.replaceAll(/\D/g, '');

    newValueForRaw[index] = parseValue.length ? Number(parseValue) : 0;
    rawValue.value = [newValueForRaw[0], newValueForRaw[1]];
    updateStore();
  };

  const updateRangeInput = (value: Array<number>) => {
    rawValue.value = [value[0] ?? 0, value[1] ?? 0];
  };

  const handleFromInputValue = (value: string | number) => {
    updateRawValue(String(value), 0);
  };

  const handleToInputValue = (value: string | number) => {
    updateRawValue(String(value), 1);
  };

  const saveOrSwapValues = (from: number, to: number) => {
    if (from > to) {
      rawValue.value = [to, from];

      return;
    }

    rawValue.value = [from, to];
  };

  const validateFromValue = () => {
    const bounds = priceBounds.value;

    if (!bounds) {
      return;
    }

    const [from, to] = rawValue.value;

    saveOrSwapValues(Math.max(from ?? bounds.min, bounds.min), to);
  };

  const validateToValue = () => {
    const bounds = priceBounds.value;

    if (!bounds) {
      return;
    }

    const [from, to] = rawValue.value;

    saveOrSwapValues(from, Math.min(to ?? bounds.max, bounds.max));
  };

  const focusOutHandler = (id: number) => {
    const bounds = priceBounds.value;

    if (!bounds) {
      return;
    }

    const currentRawValue = rawValue.value[id];

    if (!currentRawValue) {
      rawValue.value[id] = id ? bounds.max : bounds.min;

      return;
    }

    if (id) {
      validateToValue();
    } else {
      validateFromValue();
    }
  };

  const handleFromFocusOut = () => {
    focusOutHandler(0);
  };

  const handleToFocusOut = () => {
    focusOutHandler(1);
  };

  watch(
    [() => options.priceState.price, () => toValue(options.minPrice), () => toValue(options.maxPrice)],
    () => {
      rawValue.value = getRawValueFromState();
    },
    { deep: true, immediate: true },
  );

  return {
    isPriceRangeReady,
    rawValue,
    formattedPrices,
    fromPricePlaceholder,
    toPricePlaceholder,
    updateRangeInput,
    handleFromInputValue,
    handleToInputValue,
    handleFromFocusOut,
    handleToFocusOut,
  };
}
